// Utility helpers for robust CSV export/import handling

// Detect a reasonable delimiter by sampling text (fallbacks to comma)
export function detectDelimiter(sampleText) {
  const candidates = [',', ';', '\t', '|'];
  const lines = sampleText.split(/\r\n|\n|\r/).slice(0, 10).filter(Boolean);
  if (lines.length === 0) return ',';

  const scores = candidates.map((d) => {
    const counts = lines.map((l) => (l.match(new RegExp(escapeRegExp(d), 'g')) || []).length);
    const avg = counts.reduce((a, b) => a + b, 0) / counts.length;
    const variance =
      counts.reduce((a, b) => a + Math.pow(b - avg, 2), 0) / Math.max(1, counts.length - 1);
    return { d, avg, variance };
  });
  // Prefer the delimiter with highest average and low variance
  scores.sort((a, b) => b.avg - a.avg || a.variance - b.variance);
  return scores[0].avg > 0 ? scores[0].d : ',';
}

export function normalizeHeader(h) {
  return (h || '').replace(/^\uFEFF/, '').replace(/\s+/g, ' ').trim();
}

export function parseNumberStr(value) {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'string') return String(value);
  let s = value.trim();
  if (s === '') return '';
  // Remove commas as thousand separators
  s = s.replace(/,/g, '');
  // Final cleanup
  s = s.replace(/\s/g, '');
  return s;
}

export function escapeCsvValue(value, { delimiter = ',', preserveNewlines = false } = {}) {
  if (value === null || value === undefined) return '';
  const s = String(value);
  const hasSpecial =
    s.includes('"') || s.includes('\n') || s.includes('\r') || s.includes(delimiter);
  let out = s;
  if (!preserveNewlines) {
    out = out.replace(/\r\n|\r|\n/g, ' ');
  }
  out = out.replace(/"/g, '""');
  return hasSpecial || !preserveNewlines ? '"' + out + '"' : out;
}

export function buildCsv(headers, rows, opts = {}) {
  const delimiter = opts.delimiter || ',';
  const eol = opts.lineEnding === 'crlf' ? '\r\n' : '\n';
  const preserveNewlines = !!opts.preserveNewlines;
  const headerLine = headers
    .map((h) => escapeCsvValue(normalizeHeader(h), { delimiter, preserveNewlines }))
    .join(delimiter);
  const dataLines = rows
    .map((r) => r.map((c) => escapeCsvValue(c, { delimiter, preserveNewlines })).join(delimiter))
    .join(eol);
  return '\uFEFF' + headerLine + eol + dataLines;
}

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

